import { api, putRaw } from './api';

/**
 * The Promotions library (D-377): theme nights and specials, linked to games
 * by hand or by a live rule, each with the offers the fan gets. Kept beside
 * api.ts, like api-seasons.ts. Promo codes are `promotionEndpoints` in api.ts.
 */

export const PROMOTION_ICONS = [
  { code: 'STAR', label: 'Star' },
  { code: 'FIREWORKS', label: 'Fireworks' },
  { code: 'DOG', label: 'Dog' },
  { code: 'BEER', label: 'Beer' },
  { code: 'HOT_DOG', label: 'Hot dog' },
  { code: 'TICKET', label: 'Ticket' },
  { code: 'FAMILY', label: 'Family' },
  { code: 'JERSEY', label: 'Jersey' },
  { code: 'GIFT', label: 'Giveaway' },
  { code: 'MUSIC', label: 'Music' },
  { code: 'TROPHY', label: 'Trophy' },
  { code: 'HEART', label: 'Heart' },
] as const;

export const PROMOTION_CHIPS = [
  { code: 'PROMOTIONS', label: 'Promotions' },
  { code: 'FIREWORKS', label: 'Fireworks' },
  { code: 'THEME_NIGHTS', label: 'Theme Nights' },
  { code: 'FAMILY_FUN', label: 'Family Fun' },
  { code: 'GIVEAWAYS', label: 'Giveaways' },
  { code: 'SPECIALS', label: 'Specials' },
] as const;

export const OFFER_KINDS = [
  { code: 'INFO', label: 'Informational', hint: 'Fireworks, a theme, "$1 hot dogs at the stand". Nothing changes at checkout.' },
  { code: 'TICKET_DEAL', label: 'Automatic ticket deal', hint: 'Applied at checkout online and at the window on this promotion’s games, no code needed. Single-game tickets only; never combined with a promo code (the better one wins).' },
  { code: 'CONCESSION_SPECIAL', label: 'Concession or merchandise special', hint: 'An item at a special price, sold online and at the window only beside this promotion’s games.' },
  { code: 'GIVEAWAY', label: 'Giveaway', hint: '"First 1,000 fans": shown with its count. Nothing changes at checkout.' },
] as const;

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export interface GamePromotion {
  id: string;
  slug: string;
  name: string;
  headline: string | null;
  detail: string | null;
  sponsor: string | null;
  icon: string;
  image_file_id: string | null;
  image_url: string | null;
  accent_hex: string | null;
  chip: string;
  is_public: number;
  active: number;
  sort_order: number;
}

export interface PromotionOffer {
  id: string;
  offer_kind: 'INFO' | 'TICKET_DEAL' | 'CONCESSION_SPECIAL' | 'GIVEAWAY';
  label: string | null;
  detail: string | null;
  badge: string | null;
  promotion_id: string | null;
  add_on_product_id: string | null;
  giveaway_quantity: number | null;
  active: number;
  sort_order: number;
  deal_status: string | null;
  promotion_type: string | null;
  discount_scope: 'PER_TICKET' | 'PER_ORDER' | null;
  discount_basis_points: number | null;
  discount_amount_cents: number | null;
  fixed_price_cents: number | null;
  buy_quantity: number | null;
  get_quantity: number | null;
  get_discount_basis_points: number | null;
  min_quantity: number | null;
  max_quantity_per_order: number | null;
  usage_cap_total: number | null;
  usage_cap_per_customer?: number | null;
  product_name: string | null;
  product_description?: string | null;
  product_price_cents: number | null;
  product_max_per_order?: number | null;
  product_stock_limit?: number | null;
  product_active: number | null;
  /** The editor's detail (D-383): the deal's restrictions, the special's channels, and the orders it is on. */
  restrictions?: { ticket_type_codes: string[]; price_zone_codes: string[]; channels: string[] } | null;
  product_channels?: string[] | null;
  used_on_orders?: number;
}

export interface PromotionRule {
  id: string;
  season_id: string;
  weekdays: number[];
  from_date: string | null;
  to_date: string | null;
  opponent_id: string | null;
  category_id: string | null;
  exclude_playoff: number;
  exclude_if_necessary: number;
  active: number;
}

export interface PromotionGameLink {
  event_id: string;
  title: string;
  slug: string;
  status: string;
  source: 'RULE' | 'MANUAL';
  excluded: number;
  featured: number;
  sponsor_override: string | null;
  detail_override: string | null;
  opponent_name: string | null;
  starts_at: string | null;
  local_date: string | null;
  timezone: string | null;
}

export interface RulePreview {
  plan: { add: string[]; remove: string[]; kept: string[]; excluded: string[] };
  games: Record<string, { event_id: string; title: string; local_date: string | null; starts_at: string | null; opponent_name: string | null }>;
}

export interface EventPromotionLink {
  event_id: string;
  game_promotion_id: string;
  source: 'RULE' | 'MANUAL';
  excluded: number;
  featured: number;
  sponsor_override: string | null;
  detail_override: string | null;
  name: string;
  icon: string;
  active: number;
  rule_selects: boolean;
}

export type RuleInput = { season_id: string; weekdays: number[]; from_date?: string | null; to_date?: string | null; opponent_id?: string | null; category_id?: string | null; exclude_playoff: boolean; exclude_if_necessary: boolean; active?: boolean };

const base = '/api/admin/game-promotions';

export const gamePromotionEndpoints = {
  list: () => api.get<{ promotions: (GamePromotion & { games: number; upcoming: number; excluded_games: number; next_starts_at: string | null; offers: PromotionOffer[]; schedule_text: string; rules: number })[] }>(base),
  get: (id: string) => api.get<{ promotion: GamePromotion; offers: PromotionOffer[]; rules: PromotionRule[]; schedule_text: string; games: PromotionGameLink[] }>(`${base}/${id}`),
  create: (input: Record<string, unknown>) => api.post<{ promotion: GamePromotion }>(base, input),
  update: (id: string, input: Record<string, unknown>) => api.patch<{ promotion: GamePromotion }>(`${base}/${id}`, input),
  uploadImage: (id: string, file: File) => putRaw<{ promotion: GamePromotion }>(`${base}/${id}/image`, file, file.type),
  removeImage: (id: string) => api.del<{ promotion: GamePromotion }>(`${base}/${id}/image`),
  addRule: (id: string, input: RuleInput) => api.post<{ rule: PromotionRule; preview: RulePreview }>(`${base}/${id}/rules`, input),
  updateRule: (id: string, ruleId: string, input: RuleInput) => api.put<{ rule: PromotionRule; preview: RulePreview }>(`${base}/${id}/rules/${ruleId}`, input),
  deleteRule: (id: string, ruleId: string) => api.del<{ deleted: boolean; preview: RulePreview }>(`${base}/${id}/rules/${ruleId}`),
  preview: (id: string) => api.get<{ preview: RulePreview }>(`${base}/${id}/preview`),
  apply: (id: string) => api.post<{ applied: { added: number; removed: number; kept: number; excluded: number } }>(`${base}/${id}/apply`),
  addGames: (id: string, eventIds: string[]) => api.post<{ added: number }>(`${base}/${id}/games`, { event_ids: eventIds }),
  exclude: (id: string, eventId: string) => api.post<{ excluded: boolean; removed: boolean }>(`${base}/${id}/games/${eventId}/exclude`),
  include: (id: string, eventId: string) => api.post<{ included: boolean }>(`${base}/${id}/games/${eventId}/include`),
  updateLink: (id: string, eventId: string, input: { featured?: boolean; sponsor_override?: string | null; detail_override?: string | null }) => api.patch<{ updated: boolean }>(`${base}/${id}/games/${eventId}`, input),
  addOffer: (id: string, input: Record<string, unknown>) => api.post<{ offer: PromotionOffer }>(`${base}/${id}/offers`, input),
  updateOffer: (id: string, offerId: string, input: Record<string, unknown>) => api.patch<{ offer: PromotionOffer }>(`${base}/${id}/offers/${offerId}`, input),
  removeOffer: (id: string, offerId: string) => api.del<{ removed: boolean; used_on_orders: number }>(`${base}/${id}/offers/${offerId}`),
  eventLinks: (eventId: string) => api.get<{ links: EventPromotionLink[] }>(`${base}/events/${eventId}`),
  setEventLinks: (eventId: string, promotions: { game_promotion_id: string; featured: boolean; sponsor_override?: string | null; detail_override?: string | null }[]) =>
    api.put<{ links: EventPromotionLink[] }>(`${base}/events/${eventId}`, { promotions }),
};

export const iconLabel = (code: string): string => PROMOTION_ICONS.find((icon) => icon.code === code)?.label ?? code;
